"use client";

import { useMemo, useState } from "react";

import styles_1 from "../../../styles/patient-page-modules/careManagerDashboard.module.css";
import styles from "./styles.module.css";
import search from "../../../assets/images/search.png";
import infoIcon from "../../../assets/icons/info-rounded.svg";
import Table from "../../../components/table";
import Tooltip from "../../../components/shared-modules/Tooltip";
import FilterPanel from "../../../components/shared-modules/FilterDrawer/FilterDrawer";
import FilterBuilder from "../../../components/shared-modules/filter";
import { applyFilters } from "../../../components/shared-modules/filter/utils";
import {
  useStatuses,
  usePatients,
  usePriorityMetrics,
} from "../../../hooks/useMyTasksQueries";
import { formatLastCallDate } from "../../../utils/dateFormatters";
import BillingProgressGroup from "../../../components/patient-detail/billing-indicator/BillingProgressGroup";
import DeviceIconList from "../../../components/patient-detail/details-tab/DeviceIconList";
import TimeSortDropdown from "../../../components/case-manager/TimeSortDropdown";
import BackToTop from "../../../components/BackToTop/BackToTop";
import { LuListFilter } from "react-icons/lu";
import PrioritySummaryCards from "../../../components/case-manager/PrioritySummaryCards";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";

const priorityFiltersConfig = [
  {
    label: "Patient Name",
    value: "patient_name",
    type: "text",
    operators: ["contains", "is_empty", "is_not_empty"],
  },
  {
    label: "Priority Score",
    value: "priority_score",
    type: "number",
    operators: ["equals", "greater_than", "less_than"],
  },
  {
    label: "Provider",
    value: "provider_name",
    type: "text",
    operators: ["contains", "is_empty", "is_not_empty"],
  },
  {
    label: "Language",
    value: "preferred_language",
    type: "text",
    operators: ["contains", "is_empty", "is_not_empty"],
  },
  {
    label: "CCM",
    value: "ccm",
    type: "boolean",
    operators: ["is_true", "is_false"],
  },
  {
    label: "RPM",
    value: "rpm",
    type: "boolean",
    operators: ["is_true", "is_false"],
  },
  {
    label: "Reading Days",
    value: "reading_days_current_month",
    type: "number",
    operators: ["equals", "greater_than", "less_than"],
  },
  {
    label: "RPM Time (mins)",
    value: "rpm_time_in_minutes",
    type: "number",
    operators: ["greater_than", "less_than"],
  },
  {
    label: "CCM Time (mins)",
    value: "ccm_time_in_minutes",
    type: "number",
    operators: ["greater_than", "less_than"],
  },
];

const DailiesWorkflow = () => {
  const [nameSearch, setNameSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState([]);

  const { data: statuses = [], isLoading: statusesLoading } = useStatuses();
  const { data: allPatients = [], isLoading: staffLoading } = usePatients();
  const { data: priorityMetrics = {}, isLoading: priorityMetricsLoading } = usePriorityMetrics();

  const patientList = useMemo(
    () => allPatients.slice().sort((a, b) => b.priority_score - a.priority_score),
    [allPatients]
  );

  const handleRedirection = (patient) => {
    const ccm = Number(patient.ccm);
    const rpm = Number(patient.rpm);
    const patientId = patient.id;

    sessionStorage.setItem("_back_page", "my-tasks");
    sessionStorage.setItem("_pt_id", patientId);

    const targetUrl = `/patient/details/?patient_id=${patientId}&CCM=${ccm}&RPM=${rpm}`;
    // Force a full reload so the page refreshes after navigation
    window.location.assign(targetUrl);
  };

  const handleFilterClick = (statusId) => {
    if (activeFilters.includes(statusId)) {
      setActiveFilters(activeFilters?.filter((id) => id !== statusId));
    } else {
      setActiveFilters([...activeFilters, statusId]);
    }
  };

  const handleClearFilters = () => {
    setActiveFilters([]);
    setFilters([]);
    setNameSearch("");
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const filteredData = useMemo(() => {
    const searchedAndStatusFiltered = patientList?.filter((row) => {
      const fullName = `${row?.first_name} ${row?.last_name}`.toLowerCase();
      const matchesName = fullName.includes(nameSearch.toLowerCase());

      if (activeFilters?.length > 0) {
        const selectedStatusNames = activeFilters
          ?.map((filterId) => {
            const status = statuses.find((s) => s.id === filterId);
            return status ? status.name : null;
          })
          ?.filter(Boolean);

        return (
          matchesName &&
          row.statuses &&
          row.statuses.some((statusObj) => {
            return selectedStatusNames.includes(statusObj.status);
          })
        );
      }

      return matchesName;
    }) || [];

    const serializedForFilters = searchedAndStatusFiltered.map((patient) => {
      const providerFirst = patient?.provider?.first_name || "";
      const providerLast = patient?.provider?.last_name || "";
      const providerName = `${providerFirst} ${providerLast}`.trim();

      const ccmSeconds = Number(patient?.total_ccm_seconds) || 0;
      const rpmSeconds = Number(patient?.total_rpm_seconds) || 0;

      return {
        ...patient,
        patient_name: `${patient?.first_name || ""} ${patient?.last_name || ""}`.trim(),
        provider_name: providerName,
        ccm_time_in_minutes: Math.floor(ccmSeconds / 60),
        rpm_time_in_minutes: Math.floor(rpmSeconds / 60),
      };
    });

    const operatorsWithoutValue = new Set([
      "is_empty",
      "is_not_empty",
      "is_true",
      "is_false",
    ]);

    const activeAdvancedFilters = (filters || []).filter((rule) => {
      if (!rule?.field || !rule?.operator) return false;
      if (operatorsWithoutValue.has(rule.operator)) return true;

      const value = rule.value;
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;

      return true;
    });

    return applyFilters(serializedForFilters, activeAdvancedFilters);
  }, [patientList, nameSearch, activeFilters, statuses, filters]);

  const summary = useMemo(
    () => ({
      twoWaysCompleted:
        priorityMetrics.two_way_completed_this_month ?? 0,
      carePlansCompleted:
        priorityMetrics.care_plans_completed_this_month ?? 0,
      readingsReviewed: priorityMetrics.reviewed_patients ?? 0,
      totalPatients: priorityMetrics.total_patients_with_reading_work ?? 0,
      readingsReviewedPercentage: priorityMetrics.readings_reviewed_percentage ?? 0,
    }),
    [priorityMetrics]
  );

  const getProviderName = (provider) => {
    if (!provider) return "--";
    return `${provider.first_name} ${provider.last_name}`;
  };

  const getLastReading = (reading) => {
    if (reading === null || reading === undefined) return "--";
    if (reading === 0) {
      return <span style={{ color: "#63c132" }}>Today</span>;
    }
    if (reading === 1) {
      return <span style={{ color: "#63c132" }}>Yesterday</span>;
    }
    return `${reading} days ago`;
  };

  const patientTableColumns = [
    {
      key: "name",
      label: "Name",
      sorting: true,
      alignLeft: true,
      sortField: "last_name",
      render: (row) => (
        <div className={styles_1.nameWithDevices}>
          <span className={styles_1.patientName}>
            {row.first_name} {row.last_name}
          </span>
          <DeviceIconList devices={row.devices} size="small" />
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      alignLeft: true,
      sorting: true,
      sortField: "email",
      render: (row) => (
        <span style={{ fontSize: "12px", wordBreak: "break-all", whiteSpace: "normal" }}>
          {row.email || "--"}
        </span>
      ),
    },
    {
      key: "phone",
      label: "Phone",
      alignLeft: true,
      sorting: true,
      sortField: "primary_phone",
      render: (row) => row.primary_phone || "--",
    },
    {
      key: "program",
      label: "Program",
      alignLeft: true,
      sorting: false,
      render: (row) => (
        <BillingProgressGroup
          rpm={row.rpm_ccm_indicator?.rpm_indicator}
          ccm={row.rpm_ccm_indicator?.ccm_indicator}
          isLight={false}
        />
      ),
    },
    {
      key: "statuses",
      label: "Statuses",
      alignLeft: true,
      render: (row) => {
        if (!row.statuses || row.statuses.length === 0) {
          return "_ _";
        }

        return (
          <div className={styles_1.patient_status_container}>
            {row.statuses
              .filter(
                (status) =>
                  status.status !== "voice-mail" &&
                  status.status !== "two-way-communication"
              )
              .map((status, index) => (
                <div
                  key={index}
                  className={
                    status.is_default
                      ? styles_1.patient_default_status
                      : styles_1.patient_status_item
                  }
                >
                  <span className={styles_1.patient_status_tag}>
                    {status.status}
                    {status.status === "voice-mail" && status.voicemail_count
                      ? ` (${status.voicemail_count}x)`
                      : ""}
                    {status.status === "two-way-communication" && status.TWC_count
                      ? ` (${status.TWC_count}x)`
                      : ""}
                    {status.description && (
                      <Tooltip content={status.description} position="right">
                        <img
                          src={infoIcon}
                          className={styles_1.status_icon}
                          alt="info"
                        />
                      </Tooltip>
                    )}
                  </span>
                </div>
              ))}
          </div>
        );
      },
    },
    {
      key: "priority_score",
      label: "Priority Score",
      alignCenter: true,
      sorting: true,
      render: (row) => row?.priority_score ?? "--",
    },
    {
      key: "provider",
      label: "Provider",
      render: (row) => getProviderName(row.provider),
      alignLeft: true,
    },
    {
      key: "case_manager",
      label: "Case Manager",
      alignLeft: true,
      render: (row) => {
        const cm = row.case_manager;
        if (!cm || typeof cm !== "object") return "--";
        const name = `${cm.first_name || ""} ${cm.last_name || ""}`.trim();
        return name || "--";
      },
    },
    {
      key: "prefered_lenguage",
      label: "Language",
      render: (row) => row.preferred_language,
      alignLeft: true,
    },
    {
      key: "time_column",
      label: "Time (hr:mm:ss)",
      alignLeft: true,
      sorting: true,
      render: (row) => {
        const toHMS = (secs) => {
          const s = Number(secs) || 0;
          const h = Math.floor(s / 3600);
          const m = Math.floor((s % 3600) / 60);
          const sec = s % 60;
          return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
        };

        return (
          <span style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
            <strong>RPM</strong> {toHMS(row.total_rpm_seconds)}
            <span style={{ margin: "0 6px", color: "black", fontWeight: "bold" }}>|</span>
            <strong>CCM</strong> {toHMS(row.total_ccm_seconds)}
          </span>
        );
      },
      customHeader: (props) => <TimeSortDropdown {...props} />,
      customSort: (a, b, direction, extra) => {
        const sortType = extra?.type || "rpm";
        const sortKey =
          sortType === "ccm"
            ? "total_ccm_seconds"
            : "total_rpm_seconds";

        const aValue = Number(a[sortKey]) || 0;
        const bValue = Number(b[sortKey]) || 0;

        if (aValue === bValue) return 0;
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      },
    },
    {
      key: "reading_days_current_month",
      label: "Engagement",
      alignLeft: true,
      sorting: true,
      // Primary: reading days (nulls last in both directions, matching the
      // table's default null handling). Tie-break: most recent reading first
      // (days_since_last_reading ascending), never-read last.
      customSort: (a, b, direction) => {
        // ?? null: an absent field and an explicit null must compare equal
        // (strict !== below would otherwise subtract null - undefined = NaN)
        const aDays = a.reading_days_current_month ?? null;
        const bDays = b.reading_days_current_month ?? null;
        if (aDays == null && bDays != null) return 1;
        if (bDays == null && aDays != null) return -1;
        if (aDays !== bDays) {
          return direction === "asc" ? aDays - bDays : bDays - aDays;
        }
        const aLast = a.days_since_last_reading ?? Infinity;
        const bLast = b.days_since_last_reading ?? Infinity;
        if (aLast === bLast) return 0; // both-Infinity would yield NaN
        return aLast - bLast;
      },
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: "13px" }}>
          <span>Reading days: <strong>{row.reading_days_current_month ?? "--"}</strong></span>
          <span>Last Reading: <strong>{getLastReading(row.days_since_last_reading)}</strong></span>
        </div>
      ),
    },
    {
      key: "latest_call",
      label: "Last Call",
      alignLeft: true,
      sorting: true,
      render: (row) => {
        if (!row.latest_call) {
          return "_ _";
        }

        const commStatus = row.statuses?.find(
          (s) =>
            s.status === "two-way-communication" ||
            s.status === "voice-mail"
        );

        let label = "";
        let count = "";
        let bgColor = "#eee";
        let textColor = "#333";

        if (commStatus?.status === "two-way-communication") {
          label = "Two-Way ";
          count = commStatus.TWC_count ? ` (${commStatus.TWC_count}x)` : "";
          bgColor = "#D4EEE0";
          textColor = "#1A5D3D";
        }

        if (commStatus?.status === "voice-mail") {
          label = "Voicemail";
          count = commStatus.voicemail_count
            ? ` (${commStatus.voicemail_count}x)`
            : "";
          bgColor = "#F9F1D9";
          textColor = "#7E6513";
        }

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {label && (
              <span
                style={{
                  backgroundColor: bgColor,
                  color: textColor,
                  padding: "4px 10px",
                  borderRadius: "12px",
                  fontSize: "12px",
                  fontWeight: 500,
                  width: "fit-content",
                }}
              >
                {label}
                {count}
              </span>
            )}

            <span>{formatLastCallDate(row.latest_call)}</span>
          </div>
        );
      },

    },
  ];

  return (
    <>
      <div className={styles_1.patient_wrapper}>
     

        <div className={styles_1.table_container}>
          <PrioritySummaryCards
            twoWaysCompleted={summary.twoWaysCompleted}
            carePlansCompleted={summary.carePlansCompleted}
            readingsReviewed={summary.readingsReviewed}
            totalPatients={summary.totalPatients}
            readingsReviewedPercentage={summary.readingsReviewedPercentage}
            loading={staffLoading || priorityMetricsLoading}
          />

          {/* Search and Filter Controls */}
          <div
            className={styles_1.searchbar_container}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
              }}
            >
              <div
                className={styles_1.input_wrapper}
              >
                <input
                  type="text"
                  placeholder="Search "
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                />
                <img alt=" " src={search || "/placeholder.svg"} />
              </div>

              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  className={`${styles_1.filter_trigger_button} ${activeFilters.length > 0 ? styles_1.active : ""}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFilterPanelOpen((prev) => !prev);
                  }}
                >
                  Status Filters
                  {activeFilters.length > 0 && (
                    <span
                      style={{
                        backgroundColor: "var(--primary)",
                        color: "#DBEAF8",
                        borderRadius: "10px",
                        padding: "4px 4px",
                        fontSize: "10px",
                        minWidth: "18px",
                        textAlign: "center",
                      }}
                    >
                      {activeFilters.length}
                    </span>
                  )}
                  <LuListFilter size={14} />
                </button>

                <FilterPanel
                  isOpen={isFilterPanelOpen}
                  onToggle={() =>
                    setIsFilterPanelOpen((prev) => !prev)
                  }
                  statuses={statuses}
                  activeFilters={activeFilters}
                  onFilterChange={handleFilterClick}
                  onApplyFilters={(filters) => setActiveFilters(filters)}
                  filteredData={filteredData ?? []}
                  statusesLoading={statusesLoading}
                  variant="dropdown"
                />
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={`${styles_1.filter_trigger_button} ${filters.length > 0 ? styles_1.active : ""}`}
                  >
                    Advance Filters
                    {filters.length > 0 && (
                      <span
                        style={{
                          backgroundColor: "var(--primary)",
                          color: "#DBEAF8",
                          borderRadius: "10px",
                          padding: "4px 4px",
                          fontSize: "10px",
                          minWidth: "18px",
                          textAlign: "center",
                        }}
                      >
                        {filters.length}
                      </span>
                    )}
                    <LuListFilter size={14} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  sideOffset={5}
                  defaultCloseBtn
                  align="center"
                  className={styles_1.filter_container}
                >
                  <FilterBuilder
                    fieldConfig={priorityFiltersConfig}
                    initialFilters={filters}
                    onChange={handleFiltersChange}
                  />
                </PopoverContent>
              </Popover>

              {(activeFilters.length > 0 || filters.length > 0 || nameSearch) && (
                <button
                  className={styles_1.clear_all_button}
                  onClick={handleClearFilters}
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          <div className={`${styles_1.all_patients} ${styles_1.activeTable} ${styles.priorityTable}`}>
            <Table
              columns={patientTableColumns}
              data={filteredData ?? []}
              disableInternalScroll
              primaryHeader={true}
              loading={staffLoading}
              isRowClickable={true}
              onRowClick={(row) => handleRedirection(row)}
              defaultSortKey="name"
              defaultSortDirection="asc"
              onSort={(sortedData) => {
                // sortedData is the array after sorting in the table
                sessionStorage.setItem(
                  "_dp",
                  JSON.stringify(sortedData?.map((p) => p?.id))
                );
              }}
            />
          </div>
        </div>
      </div>
      <BackToTop />
    </>
  );
};

export default DailiesWorkflow;